import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { FormEvent, MouseEvent, useCallback, useEffect, useRef, useState } from "react";
import { adminService } from "@b2b/services";
import type { UserStatus } from "@b2b/services";
import {
  getProductDetailPath,
  BizcoverIndiaBrandMark,
  SearchSuggestions,
  HomepageCategoryMegaMenu,
  Category,
  HeaderNotificationsSlot,
} from "@b2b/ui-components";
import {
  ChevronDown,
  LayoutDashboard,
  LayoutGrid,
  Loader2,
  LogOut,
  Menu,
  MessageSquare,
  Search,
  User,
} from "lucide-react";
import { FaRegUserCircle } from "react-icons/fa";
import { useChatUnreadHeader } from "../../chat/ChatUnreadHeaderProvider";
import { useNotificationHeader } from "../../notifications/NotificationHeaderProvider";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@b2b/ui-components";
import {
  getCachedHeaderUser,
  invalidateHeaderUserCache,
} from "../../auth/headerUserCache";

const fontDm = {
  fontFamily:
    '-apple-system, BlinkMacSystemFont, "PingFang SC", "Microsoft YaHei", "Helvetica Neue", Helvetica, Arial, sans-serif',
} as const;

/** Primary nav pills: orange fill when the route matches (All Products / Shops / Pricing). */
function marketplaceNavPillClass(isActive: boolean) {
  return [
    "bz-nav-pill flex items-center justify-center gap-1.5 rounded-lg px-4 py-2 text-center text-[13px] font-semibold leading-snug transition-colors",
    isActive
      ? "bg-[#F97316] text-white shadow-[0px_2px_8px_rgba(249,115,22,0.22)] hover:bg-[#EA580C] hover:text-white"
      : "text-[#1A2A3D] hover:bg-orange-500 hover:text-white",
  ].join(" ");
}

export type BizcoverIndiaFigmaHeaderProps = {
  className?: string;
  searchQuery?: string;
  onSearchChange?: (value: string) => void;
  onSearchSubmit?: () => void;
  onExploreCategoriesClick?: (anchorRect?: DOMRect) => void;
  /** Wired from `AppLayout` so mobile users can open the dashboard sidebar. */
  onToggleSidebar?: () => void;
  /** Whether the sidebar drawer is expanded (mobile). */
  isSidebarOpen?: boolean;
};

/**
 * Pixel-faithful implementation of Figma Header (Main_File, node 22430:5083).
 * Wire callbacks from the parent for search / mega-menu; safe defaults are no-ops.
 */
export function BizcoverIndiaFigmaHeader({
  className = "",
  searchQuery: searchQueryProp = "",
  onSearchChange,
  onSearchSubmit,
  onExploreCategoriesClick,
  onToggleSidebar,
}: BizcoverIndiaFigmaHeaderProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [internalSearchQuery, setInternalSearchQuery] = useState("");
  const isSearchControlled = onSearchChange != null;
  const displaySearchQuery = isSearchControlled
    ? searchQueryProp
    : internalSearchQuery;

  const handleHeaderSearchChange = (value: string) => {
    if (isSearchControlled) onSearchChange(value);
    else setInternalSearchQuery(value);
  };
  const chatUnread = useChatUnreadHeader();
  const notifications = useNotificationHeader();
  const [userName, setUserName] = useState("Login");
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [headerProfileReady, setHeaderProfileReady] = useState(true);
  const [headerMeStatus, setHeaderMeStatus] = useState<UserStatus | null>(null);
  const [headerPrefillEmail, setHeaderPrefillEmail] = useState<string | null>(null);
  const [userProfileImage, setUserProfileImage] = useState<string | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [megaMenuOpen, setMegaMenuOpen] = useState(false);
  const [megaMenuHoveredCategoryId, setMegaMenuHoveredCategoryId] = useState<
    string | null
  >(null);
  const [megaMenuTopOffset, setMegaMenuTopOffset] = useState(100);
  const [megaMenuLeftOffset, setMegaMenuLeftOffset] = useState(0);
  const [headerLanguage, setHeaderLanguage] = useState("English");
  const [headerCurrency, setHeaderCurrency] = useState("U.S. Dollar");
  const [showTopUtilityBar, setShowTopUtilityBar] = useState(true);
  const scrollYPrev = useRef(0);
  const topBarCooldownUntil = useRef(0);

  useEffect(() => {
    let cancelled = false;
    const loadUser = async (force = false) => {
      const token = localStorage.getItem("bt_b2b_access_token");
      if (!token) {
        setIsAuthenticated(false);
        setHeaderProfileReady(true);
        setHeaderMeStatus(null);
        setHeaderPrefillEmail(null);
        setUserName("Login");
        setUserProfileImage(null);
        setUserRole(null);
        return;
      }

      setIsAuthenticated(true);
      setHeaderProfileReady(false);
      try {
        const headerUser = await getCachedHeaderUser({ force });
        if (cancelled) return;
        if (!headerUser) {
          setUserName("Account");
          setUserProfileImage(null);
          setUserRole(null);
          setHeaderMeStatus("active");
          setHeaderPrefillEmail(null);
          return;
        }
        setUserName(headerUser.name);
        setUserRole(headerUser.role);
        setUserProfileImage(headerUser.profileImage);
        setHeaderMeStatus(headerUser.meStatus);
        setHeaderPrefillEmail(headerUser.prefillEmail);
      } catch {
        if (!cancelled) {
          setUserName("Account");
          setUserProfileImage(null);
          setUserRole(null);
          setHeaderMeStatus(null);
          setHeaderPrefillEmail(null);
        }
      } finally {
        if (!cancelled) setHeaderProfileReady(true);
      }
    };
    void loadUser();

    const onTokenUpdated = () => {
      invalidateHeaderUserCache();
      void loadUser(true);
    };
    const onProfileUpdated = () => {
      invalidateHeaderUserCache();
      void loadUser(true);
    };
    const onStorage = (event: StorageEvent) => {
      if (event.key === "bt_b2b_access_token") {
        invalidateHeaderUserCache();
        void loadUser(true);
      }
    };
    window.addEventListener("bt_b2b_token_updated", onTokenUpdated);
    window.addEventListener("bt_b2b_profile_updated", onProfileUpdated);
    window.addEventListener("storage", onStorage);

    return () => {
      cancelled = true;
      window.removeEventListener("bt_b2b_token_updated", onTokenUpdated);
      window.removeEventListener("bt_b2b_profile_updated", onProfileUpdated);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  useEffect(() => {
    if (onExploreCategoriesClick) return;
    let active = true;
    adminService
      .getCategories(true, false, 2)
      .then((data: any) => {
        if (!active) return;
        const allCategories = Array.isArray(data) ? data : [];
        const topLevel = allCategories.filter((c: any) => !c.parentId);
        setCategories(topLevel);
      })
      .catch((err) => {
        console.error("Failed to fetch categories for mega menu:", err);
      });
    return () => {
      active = false;
    };
  }, [onExploreCategoriesClick]);

  const submitSearch = useCallback(
    (e: FormEvent) => {
      e.preventDefault();
      setShowSuggestions(false);
      if (onSearchSubmit) {
        onSearchSubmit();
        return;
      }
      const q = (
        isSearchControlled ? searchQueryProp : internalSearchQuery
      ).trim();
      if (q) navigate(`/search/${encodeURIComponent(q)}`);
      else navigate("/search");
    },
    [
      onSearchSubmit,
      isSearchControlled,
      searchQueryProp,
      internalSearchQuery,
      navigate,
    ],
  );

  /** Total unread messages (sum across threads) — same source as the badge; do not use thread count here or the label undercounts when one chat has multiple unread. */
  const unreadCount = Number(chatUnread?.totalUnread || 0);
  const unreadLabelCount = unreadCount > 99 ? "99+" : String(unreadCount);
  const computeSidebarOffset = useCallback(() => {
    if (typeof window === "undefined" || window.innerWidth < 1024) return 0;
    const sidebarEl = document.querySelector(
      '[data-app-shell-sidebar="true"]',
    ) as HTMLElement | null;
    if (!sidebarEl) return 0;
    return Math.max(0, Math.round(sidebarEl.getBoundingClientRect().right));
  }, []);
  /** Full buyer/seller/admin shell (dashboard, messages, profile settings) — only after GET /me reports `active`. */
  const fullMarketplaceAccount =
    isAuthenticated && headerProfileReady && headerMeStatus === "active";
  /** Google/email signup started but registration form not finished. */
  const resumeSignupFlow =
    isAuthenticated &&
    headerProfileReady &&
    headerMeStatus != null &&
    (headerMeStatus === "not_registered" || headerMeStatus === "pending_registration");

  const navigateCompleteSignup = () => {
    navigate("/login?tab=signup", {
      state: {
        fromOAuth: true,
        prefilledEmail: headerPrefillEmail ?? undefined,
      },
    });
  };

  const accountPagePath = !isAuthenticated
    ? "/login"
    : !fullMarketplaceAccount
      ? "/login?tab=signup"
      : userRole === "seller" || userRole === "buyer_seller"
        ? "/seller/profile_settings"
        : userRole === "super_admin" ||
            userRole === "company_admin" ||
            userRole === "admin"
          ? "/admin/profile"
          : "/buyer/profile_settings";
  const dashboardPath =
    !fullMarketplaceAccount
      ? "/login?tab=signup"
      : userRole === "seller" || userRole === "buyer_seller"
        ? "/seller/dashboard"
        : userRole === "super_admin" ||
            userRole === "company_admin" ||
            userRole === "admin"
          ? "/admin/dashboard"
          : "/buyer/dashboard";
  const roleLabel =
    userRole === "seller" || userRole === "buyer_seller"
      ? "Seller"
      : userRole === "super_admin" ||
          userRole === "company_admin" ||
          userRole === "admin"
        ? "Admin"
        : "Buyer";
  const handleLogout = () => {
    invalidateHeaderUserCache();
    localStorage.removeItem("bt_b2b_access_token");
    localStorage.removeItem("bt_b2b_refresh_token");
    localStorage.removeItem("bt_b2b_user_data");
    window.location.href = "/login";
  };
  const signInPath = "/login?tab=signin";
  const createAccountPath = "/login?tab=signup";
  const isHomePath =
    location.pathname === "/" ||
    location.pathname === "/home" ||
    location.pathname === "/bizcoverindia-home-updated";
  const normalizedPath =
    location.pathname.length > 1 && location.pathname.endsWith("/")
      ? location.pathname.slice(0, -1)
      : location.pathname;
  const adminHeaderHiddenRoutes = new Set([
    "/admin/dashboard",
    "/admin/admin_reports",
    "/admin/enquiries",
    "/admin/admin_chat",
    "/admin/users/dashboard",
    "/admin/users/search",
    "/admin/users/list",
    "/admin/products/dashboard",
    "/admin/products/list",
    "/admin/products/search",
    "/admin/create_product",
    "/admin/admin_categories",
    "/admin/media_management",
    "/admin/homepage",
    "/admin/admin_membership_plans",
    "/admin/membership_plans",
    "/admin/profile_settings",
  ]);
  const isAdminHeaderHiddenRoute =
    adminHeaderHiddenRoutes.has(normalizedPath) ||
    /^\/admin\/create_product(\/.*)?$/.test(normalizedPath);
  const isSellerOrBuyerPath =
    normalizedPath.startsWith("/seller") || normalizedPath.startsWith("/buyer");
  /** Hide language/currency/seller links/helpline row inside dashboard shells. */
  const isDashboardShellPath =
    normalizedPath.startsWith("/buyer") ||
    normalizedPath.startsWith("/seller") ||
    normalizedPath.startsWith("/admin");
  const showTopUtilityBarVisible =
    showTopUtilityBar && !isDashboardShellPath;
  const shouldHideMarketplaceNav =
    isAdminHeaderHiddenRoute || (isSellerOrBuyerPath && !isHomePath);
  const handleExploreCategoriesClick = (
    event: MouseEvent<HTMLButtonElement>,
  ) => {
    const anchorRect = event.currentTarget.getBoundingClientRect();
    if (onExploreCategoriesClick) {
      onExploreCategoriesClick(anchorRect);
      return;
    }
    const anchorOffset = Math.max(0, Math.ceil(anchorRect.bottom) + 4);
    setMegaMenuTopOffset(anchorOffset);
    setMegaMenuLeftOffset(computeSidebarOffset());
    setMegaMenuHoveredCategoryId(null);
    setMegaMenuOpen((prev) => !prev);
  };

  useEffect(() => {
    if (!megaMenuOpen) return;
    const updateMenuOffsets = () =>
      setMegaMenuLeftOffset(computeSidebarOffset());
    updateMenuOffsets();
    window.addEventListener("resize", updateMenuOffsets);
    return () => window.removeEventListener("resize", updateMenuOffsets);
  }, [megaMenuOpen, computeSidebarOffset]);

  useEffect(() => {
    if (isDashboardShellPath) return;
    scrollYPrev.current =
      typeof window !== "undefined" ? window.scrollY || window.pageYOffset : 0;
    let ticking = false;
    const COOLDOWN_MS = 340;
    const DELTA_DOWN = 16;
    const DELTA_UP = -16;
    const NEAR_TOP_PX = 14;

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        const now = Date.now();
        const y = window.scrollY || window.pageYOffset;

        if (now < topBarCooldownUntil.current) {
          scrollYPrev.current = y;
          return;
        }

        const delta = y - scrollYPrev.current;
        scrollYPrev.current = y;

        if (y <= NEAR_TOP_PX) {
          setShowTopUtilityBar((prev) => {
            if (!prev) topBarCooldownUntil.current = Date.now() + COOLDOWN_MS;
            return true;
          });
          return;
        }

        if (delta > DELTA_DOWN) {
          setShowTopUtilityBar((prev) => {
            if (prev) topBarCooldownUntil.current = Date.now() + COOLDOWN_MS;
            return false;
          });
          return;
        }

        if (delta < DELTA_UP) {
          setShowTopUtilityBar((prev) => {
            if (!prev) topBarCooldownUntil.current = Date.now() + COOLDOWN_MS;
            return true;
          });
        }
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [isDashboardShellPath]);

  return (
    <>
      <style>{`
        .bz-header {
          color: #1A2A3D;
          overflow-anchor: none;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
          background-color: #ffffff;
          background-image:
            radial-gradient(
              ellipse 100% 90% at 0% 28%,
              rgb(255 240 229 / 0.92) 0%,
              transparent 58%
            ),
            radial-gradient(
              ellipse 95% 85% at 100% 18%,
              rgb(240 247 249 / 0.9) 0%,
              transparent 55%
            );
        }
        /* Alibaba.com hero search: orange stroke (#FF6A00) */
        .bz-header .bz-search-form {
          border: 2px solid #ff6a00;
          transition: border-color 0.2s ease, box-shadow 0.2s ease;
        }
        .bz-header .bz-search-form:focus-within {
          border-color: #ff6a00;
          box-shadow: 0 0 0 3px rgba(255, 106, 0, 0.22);
        }
        .bz-logo-mark-img {
          transition: transform 0.25s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.25s ease;
        }
        .bz-logo-link:hover .bz-logo-mark-img {
          transform: scale(1.06);
          box-shadow: 0 6px 18px rgba(249, 115, 22, 0.30);
        }
        .bz-search-submit {
          background: linear-gradient(
            263deg,
            #ff2d0d 2.73%,
            #ff5e1a 26.36%,
            #ff6400 50%,
            #ff7e0d 73.64%,
            #ffab1a 97.27%
          );
          transition: filter 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;
        }
        .bz-search-submit:hover {
          filter: brightness(1.06);
          transform: translateY(-1px);
          box-shadow: 0 4px 14px rgba(255, 77, 0, 0.35);
        }
        .bz-explore-btn {
          position: relative;
          overflow: hidden;
          isolation: isolate;
        }
        .bz-explore-btn::after {
          content: "";
          position: absolute;
          top: 0;
          left: 0;
          width: 40%;
          height: 100%;
          background: linear-gradient(120deg, transparent 0%, rgba(255,255,255,0.45) 50%, transparent 100%);
          transform: translateX(-120%) skewX(-20deg);
          pointer-events: none;
          z-index: 1;
        }
        .bz-explore-btn:hover::after {
          animation: bz-header-shine 0.9s ease;
        }
        @keyframes bz-header-shine {
          0% { transform: translateX(-120%) skewX(-20deg); }
          100% { transform: translateX(220%) skewX(-20deg); }
        }
        .bz-nav-pill {
          transition: background-color 0.2s ease, color 0.2s ease, transform 0.2s ease;
        }
        .bz-nav-pill:hover {
          transform: translateY(-1px);
        }
        .bz-account-trigger {
          transition: transform 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease;
        }
        .bz-account-trigger:hover {
          transform: translateY(-1px);
          box-shadow: 0 6px 18px rgba(26, 42, 61, 0.08);
          background-color: rgba(249, 115, 22, 0.05);
        }
        .bz-account-avatar {
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }
        .bz-account-trigger:hover .bz-account-avatar {
          transform: scale(1.04);
          box-shadow: 0 6px 16px rgba(249, 115, 22, 0.26);
        }
        .bz-account-menu-item {
          transition: background-color 0.2s ease, color 0.2s ease, transform 0.2s ease;
        }
        .bz-account-menu-item:hover {
          transform: translateX(1px);
        }
        .bz-top-utility-link:hover {
          color: #1a2a3d;
        }
        @media (prefers-reduced-motion: reduce) {
          .bz-header .bz-search-form,
          .bz-logo-mark-img,
          .bz-search-submit,
          .bz-nav-pill,
          .bz-account-trigger,
          .bz-account-avatar,
          .bz-account-menu-item {
            transition: none;
          }
          .bz-logo-link:hover .bz-logo-mark-img,
          .bz-search-submit:hover,
          .bz-nav-pill:hover,
          .bz-account-trigger:hover,
          .bz-account-trigger:hover .bz-account-avatar,
          .bz-account-menu-item:hover {
            transform: none;
          }
          .bz-explore-btn:hover::after {
            animation: none;
          }
        }
      `}</style>
      <header
        className={`bz-header sticky top-0 z-50 shrink-0 flex w-full flex-col overflow-visible text-[#1A2A3D] shadow-[0_1px_0_rgba(15,23,42,0.06)] ${className}`.trim()}
        style={fontDm}
      >
        {/* Top utility row — hides on scroll down, shows on scroll up / near top */}
        <div
          className={[
            "overflow-hidden bg-gradient-to-r from-[#fff8f2] via-white to-[#eef6ff] transition-[max-height] duration-300 ease-out motion-reduce:transition-none",
            showTopUtilityBarVisible
              ? "max-h-[160px] border-b border-solid border-[#e8ecf0]"
              : "max-h-0 border-b-0",
          ].join(" ")}
          aria-hidden={!showTopUtilityBarVisible}
        >
        <div className="flex w-full flex-wrap items-center justify-between gap-x-3 gap-y-1.5 px-4 py-1.5 text-[12px] leading-4 text-[#637381] lg:px-36">
          <div className="flex min-w-0 flex-wrap items-center gap-1 sm:gap-3">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="bz-top-utility-link inline-flex items-center justify-center gap-1 rounded-md px-1.5 py-0.5 text-center text-[12px] font-normal text-[#637381] outline-none transition-colors hover:bg-black/[0.03] focus-visible:ring-2 focus-visible:ring-[#F97316] focus-visible:ring-offset-1"
                  aria-label="Select language"
                >
                  {headerLanguage}
                  <ChevronDown className="size-3.5 shrink-0 opacity-70" aria-hidden />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="start"
                className="min-w-[140px] rounded-lg border border-[#e8ecf0] bg-white p-1 shadow-md"
              >
                {(["English", "हिन्दी", "தமிழ்"] as const).map((opt) => (
                  <DropdownMenuItem
                    key={opt}
                    className="cursor-pointer rounded-md text-[13px]"
                    onClick={() => setHeaderLanguage(opt)}
                  >
                    {opt}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="bz-top-utility-link inline-flex items-center justify-center gap-1 rounded-md px-1.5 py-0.5 text-center text-[12px] font-normal text-[#637381] outline-none transition-colors hover:bg-black/[0.03] focus-visible:ring-2 focus-visible:ring-[#F97316] focus-visible:ring-offset-1"
                  aria-label="Select currency"
                >
                  {headerCurrency}
                  <ChevronDown className="size-3.5 shrink-0 opacity-70" aria-hidden />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="start"
                className="min-w-[160px] rounded-lg border border-[#e8ecf0] bg-white p-1 shadow-md"
              >
                {(["U.S. Dollar", "Indian Rupee (INR)", "Euro"] as const).map((opt) => (
                  <DropdownMenuItem
                    key={opt}
                    className="cursor-pointer rounded-md text-[13px]"
                    onClick={() => setHeaderCurrency(opt)}
                  >
                    {opt}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <div className="flex max-w-full flex-wrap items-center gap-x-1 gap-y-1 text-[12px] font-normal sm:gap-x-2">
            {resumeSignupFlow ? (
              <>
                <button
                  type="button"
                  onClick={navigateCompleteSignup}
                  className="bz-top-utility-link whitespace-nowrap px-0.5 text-left font-semibold text-[#F97316] transition-colors hover:underline"
                >
                  Complete registration
                </button>
                <span className="select-none text-[#d0d5dd]" aria-hidden>
                  |
                </span>
              </>
            ) : !isAuthenticated ? (
              <>
                <Link
                  to={createAccountPath}
                  className="bz-top-utility-link whitespace-nowrap px-0.5 text-[#637381] transition-colors"
                >
                  Become a Seller!
                </Link>
                <span className="select-none text-[#d0d5dd]" aria-hidden>
                  |
                </span>
                <Link
                  to={signInPath}
                  className="bz-top-utility-link whitespace-nowrap px-0.5 text-[#637381] transition-colors"
                >
                  Login to Seller
                </Link>
                <span className="select-none text-[#d0d5dd]" aria-hidden>
                  |
                </span>
              </>
            ) : null}
            <span className="min-w-0 text-[11px] text-[#637381] sm:text-[12px]">
              <span className="hidden sm:inline">Helpline: </span>
              <a
                href="tel:+919894399054"
                className="bz-top-utility-link whitespace-nowrap underline-offset-2 hover:underline"
              >
                +91 98943 99054
              </a>
              <span className="text-[#d0d5dd]">, </span>
              <a
                href="tel:+918524922323"
                className="bz-top-utility-link whitespace-nowrap underline-offset-2 hover:underline"
              >
                +91 85249 22323
              </a>
            </span>
          </div>
        </div>
        </div>

        {/* Logo + search */}
        <div className="relative z-[100] flex w-full flex-wrap items-center justify-between gap-3 border-b border-solid border-[#dfe3e8] px-4 py-2 sm:py-2.5 lg:px-36">
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2.5 sm:gap-5 lg:gap-8">
            {onToggleSidebar ? (
              <button
                type="button"
                onClick={onToggleSidebar}
                className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-[#dfe3e8] text-[#1A2A3D] hover:bg-black/[0.04] lg:hidden"
                aria-label="Open navigation menu"
              >
                <Menu className="size-5" aria-hidden />
              </button>
            ) : null}
            <BizcoverIndiaBrandMark
              variant="header"
              to="/bizcoverindia-home-updated"
            />

            <div
              className={`relative w-full max-w-[800px] min-w-[200px] flex-1 ${
                shouldHideMarketplaceNav ? "hidden" : ""
              }`.trim()}
            >
              <form
                onSubmit={submitSearch}
                className="bz-search-form flex h-11 w-full items-center justify-between rounded-[100px] border-solid py-0.5 pl-4 pr-1"
              >
                <input
                  type="search"
                  name="q"
                  value={displaySearchQuery}
                  onChange={(e) => {
                    handleHeaderSearchChange(e.target.value);
                    setShowSuggestions(true);
                  }}
                  onFocus={() => setShowSuggestions(true)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      setShowSuggestions(false);
                    }
                  }}
                  placeholder="Search for the Items"
                  className="min-w-0 flex-1 border-0 bg-transparent text-[13px] font-normal leading-snug text-[#1A2A3D] placeholder:text-[#919eab] focus:outline-none focus:ring-0"
                  aria-label="Search for items"
                  aria-expanded={showSuggestions}
                  aria-haspopup="listbox"
                />
                <button
                  type="submit"
                  className="bz-search-submit inline-flex h-8 shrink-0 items-center justify-center gap-1.5 rounded-full px-2.5 sm:px-3.5 text-[12px] font-semibold text-white"
                  aria-label="Search"
                >
                  <Search className="size-3.5 shrink-0 text-white" aria-hidden />
                  <span className="hidden sm:inline">Search</span>
                </button>
              </form>
              <SearchSuggestions
                searchQuery={displaySearchQuery}
                isOpen={showSuggestions}
                onClose={() => setShowSuggestions(false)}
                onSelectProduct={(productId) => {
                  setShowSuggestions(false);
                  navigate(getProductDetailPath(productId));
                }}
                onSelectSuggestion={() => setShowSuggestions(false)}
                onSearchQueryChange={(query) => handleHeaderSearchChange(query)}
              />
            </div>
          </div>

          <div className="flex shrink-0 items-center gap-4">
            {!isAuthenticated ? (
              <div className="flex items-center gap-1.5">
                <Link
                  to={createAccountPath}
                  className="inline-flex items-center justify-center rounded-full border border-[#F97316] px-3 py-1.5 text-center text-[12px] font-semibold text-[#F97316] hover:bg-[#FFF7ED]"
                >
                  Create Account
                </Link>
                <Link
                  to={signInPath}
                  className="inline-flex items-center justify-center rounded-full bg-[#F97316] px-3 py-1.5 text-center text-[12px] font-semibold text-white hover:bg-[#EA580C]"
                >
                  Sign In
                </Link>
              </div>
            ) : !headerProfileReady ? (
              <div
                className="flex h-10 items-center justify-center px-2"
                role="status"
                aria-live="polite"
                aria-busy="true"
                aria-label="Loading account"
              >
                <Loader2 className="size-7 animate-spin text-[#F97316]" />
              </div>
            ) : resumeSignupFlow ? (
              <button
                type="button"
                onClick={navigateCompleteSignup}
                className="inline-flex items-center justify-center rounded-full bg-[#F97316] px-4 py-1.5 text-center text-[12px] font-semibold text-white hover:bg-[#EA580C]"
              >
                Complete registration
              </button>
            ) : null}

            {fullMarketplaceAccount ? (
              <>
                <Link
                  to={
                    userRole === "admin" || userRole === "super_admin"
                      ? "/admin/admin_chat"
                      : "/chat"
                  }
                  className="flex items-center gap-2.5 rounded-lg outline-none ring-offset-2 hover:opacity-90 focus-visible:ring-2 focus-visible:ring-[#F97316]"
                >
                  <div className="relative flex size-10 shrink-0 items-center justify-center rounded-full bg-orange-500">
                    <span className="size-5 overflow-hidden">
                      <MessageSquare className="size-5 overflow-hidden text-white" />
                    </span>
                    {unreadCount > 0 ? (
                      <span className="absolute -right-1 -top-1 flex min-w-[16px] items-center justify-center rounded-full bg-[#dc2626] px-0.5 text-[9px] font-semibold leading-3 text-white">
                        {Number(unreadLabelCount) > 99
                          ? "99+"
                          : Number(unreadLabelCount)}
                      </span>
                    ) : null}
                  </div>
                  <div className="hidden min-w-[88px] flex-col text-left sm:flex">
                    <span className="text-[11px] font-normal leading-4 text-[#495057]">
                      Messages
                    </span>
                    <span className="text-[13px] font-normal leading-snug text-[#1A2A3D]">
                      {`Unread (${unreadLabelCount})`}
                    </span>
                  </div>
                </Link>

                <HeaderNotificationsSlot
                  notifications={notifications}
                  showNotificationsButton
                  isAuthenticated={fullMarketplaceAccount}
                  variant="figma"
                />
              </>
            ) : null}

            {fullMarketplaceAccount || resumeSignupFlow ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="bz-account-trigger flex items-center gap-2.5 rounded-lg px-1.5 py-1 outline-none ring-offset-2 focus-visible:ring-2 focus-visible:ring-[#F97316]"
                  >
                    <div className="bz-account-avatar flex size-10 shrink-0 items-center justify-center rounded-full bg-[#FFB400]">
                      {userProfileImage ? (
                        <img
                          src={userProfileImage}
                          alt={userName}
                          className="size-10 rounded-full object-cover"
                        />
                      ) : (
                        <span className="size-5 overflow-hidden">
                          <FaRegUserCircle className="size-5 text-[#1A2A3D]" />
                        </span>
                      )}
                    </div>
                    <div className="flex min-w-[52px] flex-col text-left">
                      <span className="text-[11px] font-normal leading-4 text-[#495057]">
                        {resumeSignupFlow ? "Sign-up" : `${roleLabel} Account`}
                      </span>
                      <span className="max-w-[160px] truncate text-[13px] font-semibold leading-snug text-[#1A2A3D]">
                        {userName}
                      </span>
                    </div>
                    <ChevronDown className="size-4 text-[#1A2A3D]" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  className="w-56 rounded-xl border border-[#dfe3e8] bg-white p-1 shadow-[0px_10px_30px_rgba(26,42,61,0.12)]"
                >
                  <DropdownMenuLabel className="px-2 py-1">
                    <div className="flex flex-col">
                      <span className="text-[14px] font-semibold leading-5 text-[#1A2A3D]">
                        {userName}
                      </span>
                      <span className="text-[12px] font-normal leading-5 text-[#637381]">
                        {resumeSignupFlow
                          ? "Finish your registration to continue"
                          : roleLabel}
                      </span>
                    </div>
                  </DropdownMenuLabel>
                  {resumeSignupFlow ? (
                    <>
                      <DropdownMenuItem
                        onClick={navigateCompleteSignup}
                        className="bz-account-menu-item rounded-lg text-[14px] font-medium text-[#1A2A3D] focus:bg-orange-100"
                      >
                        <LayoutDashboard className="mr-2 h-4 w-4" />
                        Continue sign-up
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={handleLogout}
                        className="bz-account-menu-item rounded-lg text-[14px] font-medium text-[#1A2A3D] focus:bg-orange-100"
                      >
                        <LogOut className="mr-2 h-4 w-4" />
                        Logout
                      </DropdownMenuItem>
                    </>
                  ) : (
                    <>
                      <DropdownMenuItem
                        onClick={() => navigate(dashboardPath)}
                        className="bz-account-menu-item rounded-lg text-[14px] font-medium text-[#1A2A3D] focus:bg-orange-100"
                      >
                        <LayoutDashboard className="mr-2 h-4 w-4" />
                        Dashboard
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => navigate(accountPagePath)}
                        className="bz-account-menu-item rounded-lg text-[14px] font-medium text-[#1A2A3D] focus:bg-orange-100"
                      >
                        <User className="mr-2 h-4 w-4" />
                        Profile
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={handleLogout}
                        className="bz-account-menu-item rounded-lg text-[14px] font-medium text-[#1A2A3D] focus:bg-orange-100"
                      >
                        <LogOut className="mr-2 h-4 w-4" />
                        Logout
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : null}
          </div>
        </div>

        {/* Primary menu — keep below search suggestions stack (logo row uses z-[100]) */}
        <div
          className={`relative z-0 flex w-full flex-wrap items-center justify-between gap-3 border-b border-solid border-[#dfe3e8] px-4 py-2 lg:px-36 ${
            shouldHideMarketplaceNav ? "hidden" : ""
          }`.trim()}
        >
          <button
            type="button"
            onClick={handleExploreCategoriesClick}
            className="bz-explore-btn inline-flex w-auto max-w-full shrink-0 items-center justify-center gap-1.5 rounded-lg bg-[#F97316] px-4 py-2 text-center shadow-[0px_6px_15px_0px_rgba(249,115,22,0.26)] outline-none ring-offset-2 hover:bg-[#EA580C] focus-visible:ring-2 focus-visible:ring-[#C2410C] sm:px-5"
          >
            <LayoutGrid className="size-3.5 shrink-0 overflow-hidden text-white" />
            <span className="whitespace-nowrap text-center text-[11px] font-semibold leading-snug text-white">
              Explore All Categories
            </span>
            <ChevronDown className="size-3.5 shrink-0 overflow-hidden text-white" />
          </button>
          <nav className="flex flex-1 flex-wrap items-center justify-start gap-x-1 gap-y-1.5 text-[13px] font-semibold leading-snug">
            <NavLink
              to="/"
              className={() => marketplaceNavPillClass(isHomePath)}
            >
              Home
            </NavLink>
            <NavLink
              to="/search"
              className={({ isActive }) => marketplaceNavPillClass(isActive)}
            >
              All Products
            </NavLink>
            <NavLink
              to="/sellers"
              className={({ isActive }) => marketplaceNavPillClass(isActive)}
            >
              Shops
            </NavLink>
            <NavLink
              to="/pricing"
              className={({ isActive }) =>
                marketplaceNavPillClass(
                  isActive || normalizedPath === "/membership-plans",
                )
              }
            >
              Pricing
            </NavLink>
          </nav>
        </div>
      </header>
      {!onExploreCategoriesClick ? (
        <HomepageCategoryMegaMenu
          categories={categories}
          isOpen={megaMenuOpen}
          onOpenChange={setMegaMenuOpen}
          hoveredCategoryId={megaMenuHoveredCategoryId}
          onHoverCategory={setMegaMenuHoveredCategoryId}
          onNavigate={(path) => navigate(path)}
          topOffsetPx={megaMenuTopOffset}
          leftOffsetPx={megaMenuLeftOffset}
        />
      ) : null}
    </>
  );
}
